import React from 'react';
import Skeleton from 'react-loading-skeleton';

import { IconTypes } from '@eika/icon';
import { LinkCard } from '@eika/link-panel';

import { useConfig } from '../../defaultConfig';
import { useAccount } from '../../fetchHooks/useAccount';
import { useDebitcardLink } from '../../fetchHooks/useCards';
import useAccountId from '../../hooks/queryparams/useAccountId';
import useTranslation from '../../hooks/useTranslation';
import { useAccountSupportsCards } from '../AccountDetails/useAccountSupportsCards';
import CardsSkeleton from './CardsSkeleton';

const CardsAndDetailsLink: React.FC = () => {
    const translated = useTranslation();
    const { accountDetailsLink } = useConfig();
    const accountId = useAccountId();
    const { data: account, isLoading: accountIsLoading } = useAccount(accountId);
    const bsuAccountType = account?.type === 'BSU' || account?.type === 'BSU_PLUSS';
    const canHaveCards = useAccountSupportsCards(account);
    const { data: debitCardUrls = [], isLoading: debitCardsLoading } = useDebitcardLink(account?.accountNumber, {
        enabled: canHaveCards && !bsuAccountType,
    });

    const isLoading = accountIsLoading || debitCardsLoading;

    if (bsuAccountType) return null;

    return (
        <section className="cards-and-detail-links__wrapper">
            {accountIsLoading && <Skeleton height={120} />}
            {!accountIsLoading && accountDetailsLink && accountId && (
                <LinkCard
                    title={translated.accountDetails.seeDetails}
                    textOne={translated.accountDetails.moreInfoAboutThisAccount}
                    icon={IconTypes.INFO_DYN}
                    data-testid="account-details-link"
                    url={`${accountDetailsLink.url}?id=${accountId.value}`}
                />
            )}

            {isLoading ? (
                <CardsSkeleton count={debitCardUrls.length} />
            ) : (
                <>
                    {debitCardUrls.length > 0 && (
                        <article className="debit-card-links__wrapper">
                            <h3 className="debit-card-links__title">
                                {translated.accountDetails.cardsConnectedToTheAccount}
                            </h3>
                            <ul className="debit-card-links__list">
                                {debitCardUrls.map((debitCard) => (
                                    <LinkCard
                                        className="debit-card-links__list-item"
                                        as="li"
                                        icon={IconTypes.BANKKORT_DYN}
                                        key={debitCard.url}
                                        title={account?.alias ?? debitCard.name}
                                        url={debitCard.url}
                                    />
                                ))}
                            </ul>
                        </article>
                    )}
                </>
            )}
        </section>
    );
};

export default CardsAndDetailsLink;
